// شريط إعلانات الإدارة — **المحتوى ظاهر من لحظة وصوله، بلا فتح ولا نقرة**.
//
// هذا هو بيت القصيد: الموظف الذي يرى «لديك رسالة» ولا يضغط عليها لا يعرف شيئًا. فالإعلان
// يُعرض هنا بنصّه كاملًا فوق الصفحة، ومعه اسم كاتبه ووقته — والنداء واحد لا نداءان: النصّ
// يصل في نفس حمولة `/notifications` التي يقرؤها الجرس أصلًا.
//
// والقراءة تبقى فعلًا صريحًا: العرض لا يَسِم الإعلان مقروءًا من تلقائه، لأن «قرأ» حالة
// يقرّرها الموظف بضغطه لا شريطٌ مرّ أمام عينه. وبعد الوسم يظهر زرّ الإخفاء — والخادم يرفض
// الإخفاء قبل القراءة على كل حال.
//
// الصوت: يُشغَّل مرّة واحدة لكل إعلان جديد، وبنغمة مولَّدة بـWebAudio لا ملفًا خارجيًا.
// والمتصفّحات تمنع الصوت قبل أول تفاعل من المستخدم — فإن مُنع لم نَعِد بشيء ولم نكسر شيئًا:
// الإعلان يبقى ظاهرًا بنصّه، والصوت تحسينٌ لا شرط.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ANNOUNCEMENT_TYPE, ApiError, get, post, qs, type AppNotification, type NotificationsResponse } from '../lib/api.ts';
import { Alert } from '../components/ui.tsx';
import { cairoDateTimeAr } from '../lib/format.ts';
import { usePulse } from '../lib/pulse.tsx';

/** كل عشرين ثانية: الإعلان خبرٌ يُلحق لا تقريرٌ يُنتظر، والنداء خفيفٌ يقرأ ما قرأه الجرس. */
const EVERY_MS = 20_000;

const TONE: Record<string, string> = {
  CRITICAL: 'border-rose-300 bg-rose-50',
  WARNING: 'border-amber-300 bg-amber-50',
  INFO: 'border-brand-200 bg-brand-50',
};

/** نغمة قصيرة مولَّدة — بلا ملف ولا اعتمادية. تفشل بصمت حيث يمنع المتصفّح الصوت. */
function ping(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === 'suspended') { void ctx.resume(); }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.36);
    osc.onended = () => { void ctx.close(); };
  } catch { /* الصوت تحسين لا شرط — لا رسالة ولا أثر */ }
}

export function AnnouncementBanner() {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seen = useRef<Set<string>>(new Set());
  const first = useRef(true);

  // مع النبضة (27/09/2026) تُقرأ الإعلانات حين يتغيّر مفتاح الإشعارات لا كل عشرين ثانية
  const pulse = usePulse();
  const hasPulse = pulse !== null && pulse.healthy;   // نبضةٌ فاشلة = بلا نبضة: يعود السؤال القديم (مراجعة 27/09/2026)
  const load = useCallback(async () => {
    try {
      const r = await get<NotificationsResponse>(`/notifications${qs({ limit: 50 })}`);
      const anns = r.items.filter(n => n.type === ANNOUNCEMENT_TYPE);
      setItems(anns);
      // صوت للجديد وحده، ولا صوت عند أول تحميل للصفحة (ليس وصولًا بل عرضٌ لما وصل سابقًا)
      const fresh = anns.filter(n => n.readAt === null && !seen.current.has(n.id));
      anns.forEach(n => seen.current.add(n.id));
      if (!first.current && fresh.length > 0) ping();
      first.current = false;
    } catch { /* الشريط تحسين عرض: فشله لا يُعطّل الصفحة ولا يُقلق الموظف */ }
  }, []);

  /**
   * **الإعلان يظهر من نفسه** (قرار المالك 21/09/2026: «عاوز الإعلان يظهر على طول بمجرد ما
   * أبعته — ليه الموظف لازم يعمل رفرش للصفحة؟»): كان الشريط يسأل مرّةً عند فتح الصفحة
   * وحدها، فمن فتح البرنامج الصبح لا يرى إعلان الظهر حتى يحدّث الصفحة بيده.
   *
   * فصار يسأل كل عشرين ثانية، **ويسأل فورًا حين تعود الصفحة إلى الواجهة** — فمن بدّل
   * التطبيق ورجع يجد الإعلان في انتظاره لا بعد عشرين ثانية أخرى. **ويصمت وهي مخفيّة**:
   * لا فائدة من سؤالٍ لا يراه أحد، وبطارية الهاتف تُحسب.
   *
   * والنداء هو نفسه الذي يقرؤه الجرس (`/notifications`) — لا مسار جديد ولا صفّ يُكتب.
   */
  useEffect(() => {
    let timer: number | undefined;
    const stop = () => { if (timer !== undefined) { window.clearInterval(timer); timer = undefined; } };
    const start = () => { stop(); if (hasPulse) return; timer = window.setInterval(() => { void load(); }, EVERY_MS); };
    const onWake = () => {
      if (document.visibilityState === 'hidden') { stop(); return; }
      void load();
      start();
    };

    void load();
    start();
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [load, hasPulse]);
  const pulseKey = pulse?.key.notifications ?? '';
  useEffect(() => { if (pulseKey) void load(); }, [pulseKey, load]);

  async function markRead(n: AppNotification) {
    setBusy(n.id); setError(null);
    try { await post(`/notifications/${n.id}/read`, {}); await load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر وسم الإعلان كمقروء'); }
    finally { setBusy(null); }
  }

  async function dismiss(n: AppNotification) {
    setBusy(n.id); setError(null);
    try { await post(`/notifications/${n.id}/dismiss`, {}); await load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر إخفاء الإعلان'); }
    finally { setBusy(null); }
  }

  if (items.length === 0) return null;

  return (
    <section className="mb-4 space-y-3" aria-label="إعلانات الإدارة">
      {error && <Alert>{error}</Alert>}
      {items.map(n => {
        const unread = n.readAt === null;
        const from = n.senderName
          ? `إعلان من ${n.senderName}${n.senderIsOwner ? ' (المالك)' : ''}`
          : 'إعلان من الإدارة';
        return (
          <article key={n.id}
            className={`rounded-xl border-2 p-4 shadow-card ${TONE[n.severity] ?? TONE.INFO} ${unread ? '' : 'opacity-80'}`}>
            <header className="flex flex-wrap items-center gap-2">
              <span aria-hidden="true">📢</span>
              <strong className="text-ink-900">{from}</strong>
              {unread && (
                <span className="rounded-full bg-rose-600 px-2 py-0.5 text-xs font-bold text-white">جديد</span>
              )}
              <span className="ms-auto text-xs tabular-nums text-ink-500">{cairoDateTimeAr(n.createdAt)}</span>
            </header>

            <h3 className="mt-2 text-lg font-bold text-ink-900">{n.title}</h3>
            {/* النصّ كاملًا — لا اقتطاع ولا «اقرأ المزيد» */}
            {n.body && <p className="mt-1 whitespace-pre-wrap text-base leading-relaxed text-ink-700">{n.body}</p>}

            <div className="mt-3 flex flex-wrap gap-2">
              {unread ? (
                <button type="button" className="btn-primary px-4 py-1.5 text-sm"
                  disabled={busy === n.id} onClick={() => void markRead(n)}>
                  {busy === n.id ? 'جارٍ…' : 'قرأت الإعلان'}
                </button>
              ) : (
                <button type="button" className="btn-ghost px-4 py-1.5 text-sm"
                  disabled={busy === n.id} onClick={() => void dismiss(n)}>
                  {busy === n.id ? 'جارٍ…' : 'إخفاء'}
                </button>
              )}
              {unread && <span className="self-center text-xs text-ink-500">الإخفاء متاح بعد تسجيل القراءة.</span>}
            </div>
          </article>
        );
      })}
    </section>
  );
}
